/**
 * The operator's card: a tutorial that is an object rather than an interface.
 *
 * Typed on a card and taped to the set by whoever left it. It never overlays the game, never
 * points at a control, and has no "next" button — it advances when the player does the thing it
 * describes (see `onboarding/steps.ts` for why that is a predicate over game state rather than a
 * click handler).
 *
 * The only control on it is "turn the card over", because a player who does not want a tutorial
 * should be able to put it down without being asked whether they are sure.
 */
import { h } from '../dom.js';
import { t } from '../../i18n/index.js';
import {
  ONBOARDING_STEPS,
  onboardingLineKey,
  onboardingState,
  type OnboardingSnapshot,
  type OnboardingStepId,
} from '../../onboarding/steps.js';

export interface OperatorCardOptions {
  /** Persist the player having turned the card over. */
  onDismiss: () => void;
}

export interface OperatorCard {
  element: HTMLElement;
  /** Re-evaluate against the world. Cheap; called from the same poll the story uses. */
  update(input: { snapshot: OnboardingSnapshot; dismissed: boolean; firstRun: boolean }): void;
}

export function createOperatorCard(options: OperatorCardOptions): OperatorCard {
  const line = h('p', { class: 'nb-card__line' });

  // One mark per step, filled as it is completed. Not a progress bar — a row of ticks on a card,
  // which is what someone leaving instructions would actually draw.
  const marks = h('div', { class: 'nb-card__marks', 'aria-hidden': 'true' });
  const markNodes = ONBOARDING_STEPS.map(() => h('span', { class: 'nb-card__mark' }));
  marks.append(...markNodes);

  const element = h(
    'aside',
    {
      class: 'nb-card',
      // A live region: the card changes on its own, and a screen-reader user must hear it do so.
      role: 'note',
      'aria-live': 'polite',
      'aria-label': t('card.title'),
      hidden: true,
    },
    h(
      'div',
      { class: 'nb-card__head' },
      h('span', { class: 'nb-card__title', text: t('card.title') }),
      h('button', {
        class: 'nb-card__dismiss',
        type: 'button',
        text: t('card.dismiss'),
        onclick: options.onDismiss,
      }),
    ),
    line,
    marks,
  );

  let shown: OnboardingStepId | null = null;

  const update: OperatorCard['update'] = (input) => {
    const state = onboardingState(input);
    element.hidden = !state.visible;
    if (!state.visible) return;

    for (const [index, mark] of markNodes.entries()) {
      mark.dataset.done = String(index < state.index);
    }

    // Only touch the text when the step actually changed: this element is `aria-live`, and
    // rewriting identical content on every poll would have a screen reader announce the same
    // line four times a second.
    if (state.step === shown) return;
    shown = state.step;
    element.dataset.step = state.step;
    line.textContent = t(onboardingLineKey(state.step) as never);
  };

  return { element, update };
}
